import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import baseURL from '../user/config';

const INACTIVITY_LIMIT = 10 * 60 * 1000; // 15 minutes

const UserProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const navigate = useNavigate();
  const inactivityTimerRef = useRef(null);

  // ✅ Logout logic
  const handleLogout = useCallback(async () => {
    try {
      await fetch(`${baseURL}/auth/user/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Logout failed:', error);
    }
    setIsAuthenticated(false);
    navigate('/user/login', { replace: true });
  }, [navigate]);

  // ✅ Auth check
  const checkAuth = useCallback(async () => {
    try {
      const response = await fetch(`${baseURL}/auth/user/check-auth`, {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) throw new Error('Unauthorized');

      const data = await response.json();
      if (data && data.user) {
        setIsAuthenticated(true);
      } else {
        handleLogout(); // Logout if no user data
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setIsAuthenticated(false);
      navigate('/user/login', { replace: true }); // Redirect on failure
    }
  }, [navigate, handleLogout]);

  // Inactivity timer logic
  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    inactivityTimerRef.current = setTimeout(() => {
      handleLogout();
    }, INACTIVITY_LIMIT);
  }, [handleLogout]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (isAuthenticated) {
      window.addEventListener('mousemove', resetInactivityTimer);
      window.addEventListener('keydown', resetInactivityTimer);
      resetInactivityTimer();

      return () => {
        window.removeEventListener('mousemove', resetInactivityTimer);
        window.removeEventListener('keydown', resetInactivityTimer);
        if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      };
    }
  }, [isAuthenticated, resetInactivityTimer]);

  // Loading state
  if (isAuthenticated === null) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          textAlign: 'center',
        }}
      >
        <CircularProgress color="primary" size={18} />
        <Typography variant="h6" sx={{ mt: 2, color: 'gray', fontSize: '0.8rem' }}>
          Loading Yamba Africa
        </Typography>
      </Box>
    );
  }

  // ✅ Access granted
  if (isAuthenticated) return children;

  // ✅ Redirect if not authenticated
  return <Navigate to="/user/login" replace />;
};

export default UserProtectedRoute;
